import { useEffect, useRef, useState } from "react";
import { Eraser, PenTool, CheckCircle2 } from "lucide-react";

/**
 * SignatureCanvas — HTML5 canvas signature pad with mouse + touch support.
 *
 * Props:
 *  - onSign: (dataUrl) => void   fired when Sign & Commit is clicked with a non-empty signature
 *  - signed: boolean             external "committed" state (disables interactions when true)
 *  - signedAt: ISO string
 */
export default function SignatureCanvas({ onSign, signed = false, signedAt = null }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  // Size the canvas to its bounding box, respecting devicePixelRatio for crisp strokes.
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const resize = () => {
      const rect = cvs.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cvs.width = Math.max(1, Math.floor(rect.width * dpr));
      cvs.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = cvs.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "#D4AF37";
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(cvs);
    return () => ro.disconnect();
  }, []);

  const pointerXY = (e) => {
    const cvs = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (signed) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointerXY(e);
  };
  const move = (e) => {
    if (!drawingRef.current || signed) return;
    e.preventDefault();
    const cvs = canvasRef.current;
    const ctx = cvs.getContext("2d");
    const { x, y } = pointerXY(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const cvs = canvasRef.current;
    const ctx = cvs.getContext("2d");
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    setHasInk(false);
  };

  const commit = () => {
    if (!hasInk || signed) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSign?.(dataUrl);
  };

  return (
    <div className="w-full" data-testid="signature-panel">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono-aviation text-slate-400 flex items-center gap-2">
          <PenTool size={11} />
          {signed ? "Signature Committed" : "Sign below with your mouse or finger"}
        </div>
        {!signed && (
          <button
            type="button"
            onClick={clear}
            data-testid="signature-clear"
            className="text-[10px] uppercase tracking-[0.24em] text-slate-400 hover:text-white inline-flex items-center gap-1"
          >
            <Eraser size={11} /> Clear
          </button>
        )}
      </div>

      <div
        className="relative rounded-sm border overflow-hidden select-none"
        style={{
          borderColor: signed ? "rgba(52,211,153,0.6)" : "rgba(212,175,55,0.45)",
          background:
            "linear-gradient(180deg, rgba(6,11,20,0.85) 0%, rgba(6,11,20,0.55) 100%)",
          height: 140,
          touchAction: "none",
        }}
      >
        {/* Baseline */}
        <div
          className="absolute left-4 right-4 bottom-6 border-b border-dashed"
          style={{ borderColor: "rgba(212,175,55,0.35)" }}
          aria-hidden
        />
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full h-full block"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          style={{ cursor: signed ? "not-allowed" : "crosshair" }}
        />
        {signed && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="text-[10px] uppercase tracking-[0.32em] font-mono-aviation text-emerald-300 flex items-center gap-2 px-3 py-1 rounded-sm border"
              style={{ background: "rgba(6,11,20,0.7)", borderColor: "rgba(52,211,153,0.6)" }}
            >
              <CheckCircle2 size={11} /> Manifesto Signed{signedAt ? ` · ${new Date(signedAt).toLocaleString()}` : ""}
            </span>
          </div>
        )}
      </div>

      {!signed && (
        <div className="mt-4">
          <button
            type="button"
            onClick={commit}
            disabled={!hasInk}
            data-testid="signature-commit"
            className="btn-gold w-full sm:w-auto px-6 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={12} />
            Sign &amp; Commit
          </button>
        </div>
      )}
    </div>
  );
}
